/**
 * Relevo de la telemetría del navegador.
 *
 * Las extensiones de privacidad (uBlock, Brave, AdGuard...) bloquean por host el envío del SDK a
 * `*.in.applicationinsights.azure.com`, así que los usuarios que las tienen son un punto ciego: sus vistas
 * de página, errores de JavaScript y tiempos nunca llegan. El SDK del navegador manda entonces sus lotes a
 * esta ruta, del mismo origen que la app, y el servidor de Next los reenvía al IngestionEndpoint de la
 * cadena de conexión. El nombre de la ruta es neutro a propósito: `track`, `telemetry` o `insights` caen
 * en los filtros por patrón de esas mismas listas.
 *
 * La ruta va sin sesión (el SDK manda vistas de página antes del login), así que solo acepta lotes cuya
 * clave de instrumentación sea la nuestra y con un tamaño acotado.
 */

/** Ruta pública del relevo; `proxy.ts` no gatea `/api`, así que no exige sesión. */
export const RUTA_RELEVO_TELEMETRIA = '/api/metricas'

/** El SDK manda lotes de decenas de KB; 1 MB deja margen sin abrir la puerta a cuerpos arbitrarios. */
export const TAMANO_MAXIMO_LOTE = 1_000_000

/** Envelope del SDK; solo los campos que el relevo lee o escribe. */
export interface IEnvelopeTelemetria {
  iKey?: string
  tags?: Record<string, unknown>
  [otro: string]: unknown
}

/**
 * El SDK serializa los lotes como arreglo JSON; en el envío al cerrar la pestaña (`sendBeacon`) pueden
 * llegar como un envelope por línea. Devuelve null si el cuerpo no es ninguna de las dos cosas.
 */
export const interpretarLote = (cuerpo: string): IEnvelopeTelemetria[] | null => {
  const texto = cuerpo.trim()

  if (!texto) {
    return null
  }

  const esEnvelope = (valor: unknown): valor is IEnvelopeTelemetria =>
    typeof valor === 'object' && valor !== null && !Array.isArray(valor)

  try {
    const parseado: unknown = JSON.parse(texto)

    if (Array.isArray(parseado)) {
      return parseado.length > 0 && parseado.every(esEnvelope) ? parseado : null
    }

    return esEnvelope(parseado) ? [parseado] : null
  } catch {
    // Un envelope por línea
  }

  const envelopes: IEnvelopeTelemetria[] = []

  for (const linea of texto.split('\n')) {
    if (!linea.trim()) {
      continue
    }
    try {
      const parseado: unknown = JSON.parse(linea)

      if (!esEnvelope(parseado)) {
        return null
      }
      envelopes.push(parseado)
    } catch {
      return null
    }
  }

  return envelopes.length > 0 ? envelopes : null
}

/** Todos los envelopes deben venir con nuestra clave: la ruta es pública y no reenvía telemetría ajena. */
export const loteEsNuestro = (envelopes: IEnvelopeTelemetria[], instrumentationKey: string): boolean =>
  envelopes.length > 0 && envelopes.every((envelope) => envelope.iKey === instrumentationKey)

/**
 * IP del navegador según las cabeceras que pone el front end de App Service. Sin ella, App Insights
 * tomaría la IP del propio App Service y todos los usuarios aparecerían en la misma ciudad.
 */
export const ipDelCliente = (headers: Headers): string | undefined => {
  const reenviada = headers.get('x-forwarded-for')

  if (reenviada) {
    // "ip_cliente:puerto, ip_proxy" → solo la primera, sin puerto
    const primera = reenviada.split(',')[0].trim()
    const ipv4ConPuerto = /^(\d{1,3}(?:\.\d{1,3}){3}):\d+$/.exec(primera)

    return ipv4ConPuerto ? ipv4ConPuerto[1] : primera
  }

  return headers.get('x-real-ip')?.trim() || undefined
}

/**
 * App Insights geolocaliza con `ai.location.ip` cuando el envelope lo trae, y después lo descarta como
 * hace con la IP del remitente. Solo se escribe si el SDK no lo puso.
 */
export const marcarIpDelCliente = (envelopes: IEnvelopeTelemetria[], ip: string | undefined): IEnvelopeTelemetria[] => {
  if (!ip) {
    return envelopes
  }

  return envelopes.map((envelope) => {
    const tags = envelope.tags ?? {}

    if (tags['ai.location.ip']) {
      return envelope
    }

    return { ...envelope, tags: { ...tags, 'ai.location.ip': ip } }
  })
}
